import React, { JSX } from "react";

import { Editor } from "@tiptap/react";
import classNames from "classnames";
import { Popup } from "components/popup";
import { useTranslation } from "react-i18next";
import {
  AiOutlineBold,
  AiOutlineItalic,
  AiOutlineLink,
  AiOutlineOrderedList,
  AiOutlineUnorderedList,
} from "react-icons/ai";
import { ImQuotesLeft } from "react-icons/im";
import { RiH1, RiH2, RiH3 } from "react-icons/ri";

export const ReducedMenuBar = ({
  editor,
  onClickLink,
  currentAdditionalInput,
}: {
  editor: Editor;
  onClickLink: () => void;
  currentAdditionalInput?: string | null;
}): JSX.Element => {
  const { t } = useTranslation("tiptapMenubar");

  return (
    <div className="rounded tiptap-editor">
      <div className="tiptap-editor-header">
        <Popup content={<p className="py-1">{t("bold")}</p>}>
          <BoldButton editor={editor} />
        </Popup>
        <Popup content={<p className="py-1">{t("italic")}</p>}>
          <ItalicButton editor={editor} />
        </Popup>
        <Popup content={<p className="py-1">{t("link")} </p>}>
          <LinkButton
            onClick={onClickLink}
            currentAdditionalInput={currentAdditionalInput}
          />
        </Popup>
        <Popup content={<p className="py-1">{t("heading1")}</p>}>
          <HeadingButton level={1} editor={editor} />
        </Popup>
        <Popup content={<p className="py-1">{t("heading2")}</p>}>
          <HeadingButton level={2} editor={editor} />
        </Popup>
        <Popup content={<p className="py-1">{t("heading3")}</p>}>
          <HeadingButton level={3} editor={editor} />
        </Popup>
        <Popup content={<p className="py-1">{t("unorderedList")}</p>}>
          <UnorderedListButton editor={editor} />
        </Popup>
        <Popup content={<p className="py-1">{t("orderedList")}</p>}>
          <OrderedListButton editor={editor} />
        </Popup>
        <Popup content={<p className="py-1">{t("quotes")}</p>}>
          <QuotesButton editor={editor} />
        </Popup>
      </div>
    </div>
  );
};

// Buttons

const BoldButton = ({ editor }) => {
  return (
    <button
      onClick={() => editor.chain().focus().toggleBold().run()}
      className={classNames("menu-item", {
        "menu-item-active": editor.isActive("bold"),
      })}
    >
      <AiOutlineBold />
    </button>
  );
};

const ItalicButton = ({ editor }) => {
  return (
    <button
      onClick={() => editor.chain().focus().toggleItalic().run()}
      className={classNames("menu-item", {
        "menu-item-active": editor.isActive("italic"),
      })}
    >
      <AiOutlineItalic />
    </button>
  );
};

const LinkButton = ({ onClick, currentAdditionalInput }) => {
  return (
    <button
      onClick={onClick}
      className={classNames("menu-item", {
        "menu-item-active": currentAdditionalInput == "link",
      })}
    >
      <AiOutlineLink />
    </button>
  );
};

const HeadingButton = ({ level, editor }) => {
  return (
    <button
      onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
      className={classNames("menu-item", {
        "menu-item-active": editor.isActive("heading", { level }),
      })}
    >
      {level === 1 ? (
        <RiH1 />
      ) : level === 2 ? (
        <RiH2 />
      ) : level === 3 ? (
        <RiH3 />
      ) : null}
    </button>
  );
};

const UnorderedListButton = ({ editor }) => {
  return (
    <button
      onClick={() => editor.chain().focus().toggleBulletList().run()}
      className={classNames("menu-item", {
        "menu-item-active": editor.isActive("bulletList"),
      })}
    >
      <AiOutlineUnorderedList />
    </button>
  );
};

const OrderedListButton = ({ editor }) => {
  return (
    <button
      onClick={() => editor.chain().focus().toggleOrderedList().run()}
      className={classNames("menu-item", {
        "menu-item-active": editor.isActive("orderedList"),
      })}
    >
      <AiOutlineOrderedList />
    </button>
  );
};

const QuotesButton = ({ editor }) => {
  return (
    <button
      onClick={() => editor.chain().focus().toggleBlockquote().run()}
      className={classNames("menu-item", {
        "menu-item-active": editor.isActive("blockquote"),
      })}
    >
      <ImQuotesLeft />
    </button>
  );
};
